import React, { useEffect, useState, useCallback, useRef } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import axios from 'axios';
import {
  Box,
  Typography,
  Paper,
  LinearProgress,
  Fade,
  Button,
} from '@mui/material';
import {
  ErrorOutline as ErrorIcon,
  CloudCircle as CloudIcon,
  WarningAmber as WarningIcon
} from '@mui/icons-material';
import { theme } from './theme';
import Dashboard from './components/Dashboard';
import { TourProvider } from './contexts/TourContext';

// Token refresh configuration
const TOKEN_REFRESH_INTERVAL = 50 * 60 * 1000; // Refresh every 50 minutes (before 1-hour expiry)
const LOGIN_URL = 'https://www.suprsketch.com/login';

// Flag to prevent multiple simultaneous refresh attempts
let isRefreshing = false;
let refreshSubscribers = [];

// Subscribe to token refresh completion
const subscribeTokenRefresh = (callback) => {
  refreshSubscribers.push(callback);
};

// Notify all subscribers that refresh is complete
const onTokenRefreshed = () => {
  refreshSubscribers.forEach(callback => callback());
  refreshSubscribers = [];
};

// Setup axios interceptor for automatic token refresh on 401 errors
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // If error is 401 and we haven't already tried to refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      // Don't retry refresh endpoint itself or login-related requests
      if (originalRequest.url?.includes('/auth/refresh')) {
        return Promise.reject(error);
      }

      if (isRefreshing) {
        // Wait for the ongoing refresh to complete, then retry
        return new Promise((resolve) => {
          subscribeTokenRefresh(() => {
            resolve(axios(originalRequest));
          });
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // Attempt to refresh the token
        const refreshResponse = await axios.post('/api/auth/refresh', {}, {
          withCredentials: true
        });

        if (refreshResponse.data.success) {
          console.log('Token refreshed successfully');
          isRefreshing = false;
          onTokenRefreshed();

          // Retry the original request
          return axios(originalRequest);
        }
      } catch (refreshError) {
        console.error('Token refresh failed:', refreshError);
        isRefreshing = false;
        refreshSubscribers = [];

        // Check if we need to redirect to login
        if (refreshError.response?.data?.requiresLogin) {
          window.location.href = `${LOGIN_URL}?redirect=${encodeURIComponent(window.location.href)}`;
          return Promise.reject(refreshError);
        }
      }
    }

    return Promise.reject(error);
  }
);

// Simple loading screen for regular loading process
const LoadingScreen = () => (
  <Fade in={true} timeout={800}>
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: 'background.default',
      }}
    >
      <Paper
        elevation={8}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          padding: 4,
          borderRadius: 2,
          minWidth: '300px',
          backgroundColor: '#1E1E1E',
          border: '1px solid rgba(255, 255, 255, 0.05)',
        }}
      >
        <Box
          sx={{
            width: 64,
            height: 64,
            borderRadius: '50%',
            backgroundColor: 'primary.main',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            mb: 3
          }}
        >
          <CloudIcon
            sx={{
              color: 'white',
              fontSize: 32
          }}
        />
        </Box>
        
        <Typography variant="h5" gutterBottom sx={{ mb: 3, fontWeight: 600, color: 'white' }}>
          Loading SuprDrive
        </Typography>
        
        <Box sx={{ width: '100%', mb: 2 }}>
          <LinearProgress
            sx={{
              height: 6,
              borderRadius: 3,
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              '& .MuiLinearProgress-bar': {
                backgroundColor: 'primary.main',
              }
            }}
          />
        </Box>
        
        <Typography variant="body2" color="text.secondary">
          Connecting to your workspace...
        </Typography>
      </Paper>
    </Box>
  </Fade>
);

// Custom unauthorized access screen
const UnauthorizedScreen = () => (
  <Fade in={true} timeout={800}>
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: 'background.default',
      }}
    >
      <Paper
        elevation={8}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          padding: 4,
          borderRadius: 3,
          maxWidth: '450px',
          backgroundColor: 'background.paper',
          border: '1px solid',
          borderColor: 'warning.main',
        }}
      >
        <WarningIcon
          sx={{
            fontSize: 60,
            color: 'warning.main',
            mb: 3,
          }}
        />
        
        <Typography variant="h5" gutterBottom sx={{ mb: 2, fontWeight: 600, textAlign: 'center' }}>
          Please Access SuprDrive through User Dashboard
        </Typography>
        
        <Typography 
          variant="body1" 
          color="text.secondary" 
          align="center"
          sx={{ mb: 3 }}
        >
          For security reasons, you must access your SuprDrive through the SuprSketch User Dashboard.
        </Typography>
        
        <Button 
          variant="contained" 
          color="primary"
          href="https://www.suprsketch.com/"
          sx={{ 
            minWidth: '200px',
            mb: 2
          }}
        >
          Go to Dashboard
        </Button>
        
        <Typography variant="body2" color="text.disabled" align="center">
          If you're experiencing issues, please contact support.
        </Typography>
      </Paper>
    </Box>
  </Fade>
);

const ErrorScreen = ({ message }) => (
  <Fade in={true} timeout={800}>
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: 'background.default',
      }}
    >
      <Paper
        elevation={8}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          padding: 4,
          borderRadius: 3,
          maxWidth: '400px',
          backgroundColor: 'background.paper',
          border: '1px solid',
          borderColor: 'error.main',
        }}
      >
        <ErrorIcon
          sx={{
            fontSize: 60,
            color: 'error.main',
            mb: 3,
          }}
        />
        
        <Typography variant="h5" gutterBottom sx={{ mb: 1, fontWeight: 600 }}>
          Connection Error
        </Typography>
        
        <Typography 
          variant="body1" 
          color="text.secondary" 
          align="center"
          sx={{ mb: 3 }}
        >
          {message}
        </Typography>
        
        <Typography variant="body2" color="text.disabled" align="center">
          Please refresh the page or contact support if the issue persists.
        </Typography>
      </Paper>
    </Box>
  </Fade>
);

function App() {
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [unauthorized, setUnauthorized] = useState(false);
  const refreshTimerRef = useRef(null);

  // Proactive token refresh function
  const refreshToken = useCallback(async () => {
    try {
      const response = await axios.post('/api/auth/refresh', {}, {
        withCredentials: true
      });

      if (response.data.success) {
        console.log('Proactive token refresh successful');
      }
    } catch (err) {
      console.error('Proactive token refresh failed:', err);

      // If refresh token is invalid, redirect to login
      if (err.response?.data?.requiresLogin) {
        window.location.href = `${LOGIN_URL}?redirect=${encodeURIComponent(window.location.href)}`;
      }
    }
  }, []);

  // Setup proactive token refresh timer
  useEffect(() => {
    if (userData) {
      // Start the refresh timer when user is authenticated
      console.log('Starting token refresh timer (every 50 minutes)');

      // Clear any existing timer
      if (refreshTimerRef.current) {
        clearInterval(refreshTimerRef.current);
      }

      // Set up periodic refresh (every 50 minutes, before 1-hour expiry)
      refreshTimerRef.current = setInterval(refreshToken, TOKEN_REFRESH_INTERVAL);

      // Cleanup on unmount or when userData changes
      return () => {
        if (refreshTimerRef.current) {
          clearInterval(refreshTimerRef.current);
          refreshTimerRef.current = null;
        }
      };
    }
  }, [userData, refreshToken]);

  useEffect(() => {
    // Standalone mode - get user data directly from auth API
    const fetchUserData = async () => {
      try {
        // API call will use cookie automatically (withCredentials sends cookies)
        const response = await axios.get('/api/auth/user', {
          withCredentials: true
        });

        if (response.data && response.data.user) {
          setUserData(response.data.user);
          setLoading(false);
        } else {
          window.location.href = `${LOGIN_URL}?redirect=${encodeURIComponent(window.location.href)}`;
        }
      } catch (err) {
        console.error('Auth error:', err);
        if (err.response && err.response.status === 401) {
          // Not authenticated - redirect to login
          window.location.href = `${LOGIN_URL}?redirect=${encodeURIComponent(window.location.href)}`;
        } else if (err.response && err.response.status === 403) {
          // Not authorized (no SuprArtist plan)
          setUnauthorized(true);
          setLoading(false);
        } else {
          setError('Failed to load user data');
          setLoading(false);
        }
      }
    };

    fetchUserData();
  }, []);

  // Render unauthorized access screen
  if (unauthorized) {
    return <UnauthorizedScreen />;
  }

  // Render loading state
  if (loading) {
    return <LoadingScreen />;
  }

  // Render error state
  if (error) {
    return <ErrorScreen message={error} />;
  }

  // Render main application
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <TourProvider>
      <Dashboard user={userData} />
      </TourProvider>
    </ThemeProvider>
  );
}

export default App;
